import { useCallback } from "react";
import { useStoreState } from "ezStore";
import useUserPreferences from "components/hooks/useUserPreferences";
import { ColumnSettingsProps, Sizes, SortList } from "@cofano/cck";
import { FilterSettingsProviderProps as UserFilterSettings } from "@cofano/cck/Filter/FilterSettingsProvider";
import { getPermission } from "imports/Utils";

type UserTableSettings = {
	columnSettings: Array<ColumnSettingsProps>,
	size: keyof typeof Sizes,
	sort?: SortList,
	pageSize: number,
}

const useSettingsProvider = () => {
	const session = useStoreState(store => store.session);
	const saveUserPreferences = useUserPreferences();

	const handleSaveSettings = useCallback((id: string, body: UserFilterSettings | UserTableSettings) => {
		saveUserPreferences({
			preferences: {
				[id]: body,
			},
		});
	}, []);

	const handleResetSettings = useCallback((id: string, doReload: boolean) => {
		const currentPreferences = (session.user?.properties.userPreferences || {}) as Record<string, unknown>;

		if (currentPreferences?.[id]) {
			delete currentPreferences[id];
		}

		saveUserPreferences({
			preferences: currentPreferences,
			successCallback: () => {
				if (doReload) {
					window.location.reload();
				}
			},
		});
	}, []);

	const getDefaultColumnSettings = (id: string) => {
		const permission = getPermission(session);
		const tableKey = id.split("cckTable_")[1];
		const defaultSettings = session.tableSettings?.find(it => it.tableKey === tableKey && it.permission === permission);
		return defaultSettings?.tableSettings as ColumnSettingsProps[] | undefined;
	};

	return {
		filterSettingsProvider: {
			settings: (id: string) => session.user?.properties.userPreferences?.[id] as UserFilterSettings,
			onSave: (id: string, settings: UserFilterSettings) => handleSaveSettings(id, settings),
			onReset: (id: string) => handleResetSettings(id, false),
		},
		tableSettingsProvider: {
			settings: (id: string) => {
				const settings = session.user?.properties.userPreferences?.[id] as UserTableSettings | undefined;
				const defaultColumnSettings = getDefaultColumnSettings(id);

				return {
					columnSettings: settings?.columnSettings || defaultColumnSettings || [],
					size: settings?.size ?? "NORMAL",
					pageSize: settings?.pageSize ?? undefined,
					sort: settings?.sort ?? undefined,
				};
			},

			onSave: (id: string, columnSettings: Array<ColumnSettingsProps>, size: keyof typeof Sizes, pageSize: number, sort?: SortList) => {
				handleSaveSettings(id, {
					columnSettings: columnSettings,
					size: size,
					pageSize: pageSize,
					sort: sort,
				});
			},
			onReset: (id: string) => {
				const defaultColumnSettings = getDefaultColumnSettings(id);
				handleResetSettings(id, !!defaultColumnSettings);
			},
		},
	};
};

export default useSettingsProvider;
